import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import {
  UserPlusIcon,
  ChartBarSquareIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  CircleStackIcon,
  AdjustmentsHorizontalIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import logger from '../../../../utils/logger';

// Toggle switch component
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
      checked ? 'translate-x-4' : 'translate-x-0'
    }`} />
  </button>
);

// Feature toggle row
const FeatureRow = ({ label, description, checked, onChange, indent }) => (
  <div className={`flex items-center justify-between py-2.5 ${indent ? 'ml-5 pl-3 border-l-2 border-gray-200 dark:border-gray-700' : ''}`}>
    <div>
      <p className={`text-sm ${indent ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>{label}</p>
      {description && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{description}</p>}
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

// Capability card
const CapabilityCard = ({ icon: Icon, title, description, enabled, onToggle, children, color = 'blue' }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
    purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
    green: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
    cyan: { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-200 dark:border-cyan-800' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className={`rounded-xl border ${enabled ? c.border : 'border-gray-200 dark:border-gray-800'} bg-white dark:bg-[#1a1a1a] overflow-hidden transition-all`}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${enabled ? c.bg : 'bg-gray-100 dark:bg-gray-800'}`}>
            <Icon className={`w-4 h-4 ${enabled ? c.text : 'text-gray-400 dark:text-gray-500'}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <Toggle checked={enabled} onChange={onToggle} />
      </div>
      {enabled && children && (
        <div className="px-4 pb-3 border-t border-gray-100 dark:border-gray-800 pt-2">
          {children}
        </div>
      )}
    </div>
  );
};

const SalesCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/sales`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching sales capabilities:', error);
      showNotification?.('Failed to load sales capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/sales`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('Sales capabilities saved', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving sales capabilities:', error);
      showNotification?.('Failed to save sales capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-16 bg-gray-100 dark:bg-[#222222] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!capabilities) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        No sales capabilities configured for this agent.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Master Toggle */}
      <div className={`rounded-xl p-4 border ${
        capabilities.enabled
          ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
          : 'bg-gray-50 dark:bg-[#1a1a1a] border-gray-200 dark:border-gray-800'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${capabilities.enabled ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
              <AdjustmentsHorizontalIcon className={`w-5 h-5 ${capabilities.enabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Sales Capabilities</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Lead management, opportunities, quotes, and CRM
              </p>
            </div>
          </div>
          <Toggle
            checked={capabilities.enabled || false}
            onChange={(val) => updateCapability('enabled', val)}
          />
        </div>
      </div>

      {capabilities.enabled && (
        <div className="space-y-3">
          {/* Lead Management */}
          <CapabilityCard
            icon={UserPlusIcon}
            title="Lead Management"
            description="Capture, qualify, and assign leads"
            color="blue"
            enabled={capabilities.leadManagement?.enabled || false}
            onToggle={(val) => updateCapability('leadManagement.enabled', val)}
          >
            <div className="space-y-0.5">
              <FeatureRow
                label="Auto-qualify leads"
                description="Score and qualify leads automatically"
                checked={capabilities.leadManagement?.autoQualify || false}
                onChange={(val) => updateCapability('leadManagement.autoQualify', val)}
              />
              <FeatureRow
                label="Lead scoring"
                checked={capabilities.leadManagement?.leadScoring?.enabled || false}
                onChange={(val) => updateCapability('leadManagement.leadScoring.enabled', val)}
              />
              {capabilities.leadManagement?.leadScoring?.enabled && (
                <div className="ml-5 pl-3 border-l-2 border-gray-200 dark:border-gray-700 py-2">
                  <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Qualification Threshold
                  </label>
                  <div className="flex items-center gap-3 mt-1.5">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={capabilities.leadManagement?.leadScoring?.threshold || 70}
                      onChange={(e) => updateCapability('leadManagement.leadScoring.threshold', Number(e.target.value))}
                      className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white w-10 text-right">
                      {capabilities.leadManagement?.leadScoring?.threshold || 70}%
                    </span>
                  </div>
                </div>
              )}
              <FeatureRow
                label="Auto-assign to reps"
                description="Route leads to available sales reps"
                checked={capabilities.leadManagement?.autoAssignReps || false}
                onChange={(val) => updateCapability('leadManagement.autoAssignReps', val)}
              />
            </div>
          </CapabilityCard>

          {/* Opportunity Management */}
          <CapabilityCard
            icon={ChartBarSquareIcon}
            title="Opportunity Management"
            description="Track deals through your pipeline"
            color="purple"
            enabled={capabilities.opportunityManagement?.enabled || false}
            onToggle={(val) => updateCapability('opportunityManagement.enabled', val)}
          >
            <div className="space-y-0.5">
              <FeatureRow
                label="Stage tracking"
                description="Track opportunities through pipeline stages"
                checked={capabilities.opportunityManagement?.stageTracking || false}
                onChange={(val) => updateCapability('opportunityManagement.stageTracking', val)}
              />
              <FeatureRow
                label="Auto-progress stages"
                description="Move deals forward automatically based on signals"
                checked={capabilities.opportunityManagement?.autoProgressStages || false}
                onChange={(val) => updateCapability('opportunityManagement.autoProgressStages', val)}
              />
              <FeatureRow
                label="Win probability"
                description="Calculate likelihood of closing"
                checked={capabilities.opportunityManagement?.winProbabilityCalc || false}
                onChange={(val) => updateCapability('opportunityManagement.winProbabilityCalc', val)}
              />
            </div>
          </CapabilityCard>

          {/* Quote Generation */}
          <CapabilityCard
            icon={DocumentTextIcon}
            title="Quote Generation"
            description="Create and send sales quotes"
            color="amber"
            enabled={capabilities.quoteGeneration?.enabled || false}
            onToggle={(val) => updateCapability('quoteGeneration.enabled', val)}
          >
            <FeatureRow
              label="Require approval"
              description="Manager must approve before sending quotes"
              checked={capabilities.quoteGeneration?.requireApproval || false}
              onChange={(val) => updateCapability('quoteGeneration.requireApproval', val)}
            />
          </CapabilityCard>

          {/* Meeting Scheduling */}
          <CapabilityCard
            icon={CalendarDaysIcon}
            title="Meeting Scheduling"
            description="Book and manage sales meetings"
            color="green"
            enabled={capabilities.meetingScheduling?.enabled || false}
            onToggle={(val) => updateCapability('meetingScheduling.enabled', val)}
          >
            <div className="space-y-0.5">
              <FeatureRow
                label="Auto-schedule"
                description="Book meetings without manual intervention"
                checked={capabilities.meetingScheduling?.autoSchedule || false}
                onChange={(val) => updateCapability('meetingScheduling.autoSchedule', val)}
              />
              <FeatureRow
                label="Calendar sync"
                description="Check rep availability before booking"
                checked={capabilities.meetingScheduling?.availabilitySync || false}
                onChange={(val) => updateCapability('meetingScheduling.availabilitySync', val)}
              />
            </div>
          </CapabilityCard>

          {/* CRM Integration */}
          <CapabilityCard
            icon={CircleStackIcon}
            title="CRM Integration"
            description="Connect to your CRM system"
            color="cyan"
            enabled={capabilities.crmIntegration?.enabled || false}
            onToggle={(val) => updateCapability('crmIntegration.enabled', val)}
          >
            <div className="space-y-2">
              <div>
                <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  CRM System
                </label>
                <select
                  value={capabilities.crmIntegration?.system || ''}
                  onChange={(e) => updateCapability('crmIntegration.system', e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select CRM...</option>
                  <option value="tellency_erp">Tellency ERP (Built-in)</option>
                  <option value="salesforce">Salesforce</option>
                  <option value="hubspot">HubSpot</option>
                  <option value="pipedrive">Pipedrive</option>
                  <option value="zoho">Zoho CRM</option>
                </select>
              </div>
              <FeatureRow
                label="Auto-log activities"
                description="Automatically log emails, calls, meetings to CRM"
                checked={capabilities.crmIntegration?.autoLogActivities || false}
                onChange={(val) => updateCapability('crmIntegration.autoLogActivities', val)}
              />
              <div className="py-2">
                <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Sync Frequency
                </label>
                <div className="flex items-center gap-3 mt-1.5">
                  <input
                    type="range"
                    min="5"
                    max="60"
                    step="5"
                    value={capabilities.crmIntegration?.syncFrequency || 15}
                    onChange={(e) => updateCapability('crmIntegration.syncFrequency', Number(e.target.value))}
                    className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white w-16 text-right">
                    {capabilities.crmIntegration?.syncFrequency || 15} min
                  </span>
                </div>
              </div>
            </div>
          </CapabilityCard>
        </div>
      )}

      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-0 flex items-center justify-between bg-white dark:bg-[#1a1a1a] border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6 px-6 py-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Unsaved changes</p>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white dark:border-gray-900 border-t-transparent dark:border-t-transparent" />
              ) : (
                <CheckIcon className="w-3.5 h-3.5" />
              )}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCapabilities;
